import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { shippingService } from "../services/dataService";
import { ShipmentDetail, ShipmentListItem, ShipmentMode, ShipmentStatus as S } from "../types";
import { usePermissions } from "../context/PermissionContext";
import {
  DESPATCH_KIND_LABEL, DESPATCH_OUTCOME_LABEL, DESPATCH_OUTCOME_TONE, DESPATCH_STATE_LABEL, DESPATCH_STATE_TONE, EPS, RA_LABEL, RA_TONE,
  SH_MODE_LABEL, SH_STATUS_CLASS, shOrderNo, SH_STATUS_LABEL, ShAge, shAge, shDriverNames, shErr, shFmtDate, shFmtDateTime, shFmtQty, shLayerBlocked,
  despatchBlocksCancel, parseAdviceSummary,
} from "../utils/shipping";
import { firmHue, firmInitials } from "./FirmPicker";
import "../pages/PurchaseOrders.css";
import "../pages/Shipping.css";

/**
 * Sevkiyat kartı (çekmece) — salt okunur belge görünümü. İşlemleri kendisi yapmaz;
 * sayfadan ister (düzenle / sevk et / teslim / iptal / sil / yazdır), böylece onay ve bildirimler tek yerde durur.
 */
type HeadInfo = Pick<ShipmentListItem, "number" | "firmName" | "firmCode" | "status" | "shipmentDate" | "shippedAt" | "waybillNo" | "mode" | "despatchNumber" | "despatchState" | "receiptAdviceState">;

/** e-İrsaliye işlemleri: durum yenile, yeniden gönder (GİB 1215/1230), yeni e-İrsaliye, matbudan dönüştür. */
export type DespatchActionKind = "refresh" | "resend" | "reissue" | "matbudan";

export interface ShipmentDrawerProps {
  shipmentId: string;
  /** Liste satırı: ayrıntı yüklenirken başlık anında dolar. */
  preview?: ShipmentListItem;
  /** Sayfa her değişiklikten sonra artırır → ayrıntı yeniden çekilir. */
  refreshKey: number;
  busy?: boolean;
  escDisabled?: boolean;
  /** Sistem ayarı: e-İrsaliye açık (taslakta bilgi notu) */
  eDespatch?: boolean;
  onClose: () => void;
  onEdit: (d: ShipmentDetail) => void;
  onShip: (d: ShipmentDetail) => void;
  onDeliver: (d: ShipmentDetail) => void;
  onCancel: (d: ShipmentDetail) => void;
  onDelete: (o: { shipmentId: string; number: string }) => void;
  onPrint: (o: { shipmentId: string; number: string; despatchNumber?: string | null }) => void;
  onDespatch?: (d: ShipmentDetail, kind: DespatchActionKind) => void;
}

const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" /></svg>,
  receive: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M7 10l5 5 5-5M4 19h16" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>,
  print: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><path d="M6 14h12v7H6z" /></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12a8 8 0 1 1-2.3-5.6" /><path d="M20 4v5h-5" /></svg>,
  doc: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3h9l4 4v14H6z" /><path d="M15 3v4h4M9 12h6M9 16h6" /></svg>,
  lock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>,
};

const NO_AGE: ShAge = { days: null, state: "", stuck: false, text: "" };

const ShipmentDrawer: React.FC<ShipmentDrawerProps> = ({ shipmentId, preview, refreshKey, busy, escDisabled, eDespatch, onClose, onEdit, onShip, onDeliver, onCancel, onDelete, onPrint, onDespatch }) => {
  const navigate = useNavigate();
  const { has, hasModule } = usePermissions();
  const [shipment, setShipment] = useState<ShipmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const overlayRef = useRef<HTMLDivElement>(null);
  const asideRef = useRef<HTMLElement>(null);
  const gen = useRef(0);

  const load = useCallback(async () => {
    const g = ++gen.current;
    setLoading(true); setError("");
    try {
      const d = await shippingService.shipment(shipmentId);
      if (g === gen.current) setShipment(d);
    } catch (e: any) {
      if (g === gen.current) { setShipment(null); setError(e?.response?.status === 404 ? "" : shErr(e, "")); }
    } finally { if (g === gen.current) setLoading(false); }
  }, [shipmentId]);
  // Başka bir sevkiyata geçilince eski kart gösterilmesin; aynı sevkiyatta yenilemede eski içerik kalır
  useEffect(() => { setShipment((s) => (s && s.shipmentId === shipmentId ? s : null)); }, [shipmentId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  // aria-modal kart odağı alır (derin bağlantıda da); kapanınca odak açıldığı yere döner
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    return () => { if (prev && prev.isConnected) prev.focus?.({ preventScroll: true }); };
  }, []);
  useEffect(() => {
    const el = asideRef.current;
    if (el && !el.contains(document.activeElement)) el.focus({ preventScroll: true });
  }, [shipmentId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented && !escDisabled && !shLayerBlocked(overlayRef.current)) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, escDisabled]);

  const h: HeadInfo | null = shipment ?? preview ?? null;
  const age = h ? shAge(h.shippedAt, h.status) : NO_AGE;
  const canUpdate = has("shipping:update");
  const canDelete = has("shipping:delete");
  const stockOn = hasModule("stock");

  const lines = shipment?.lines ?? [];
  // Birim bazında toplam: "1.250 Adet · 40 Metre"
  const unitTotals = Array.from(lines.reduce((m, l) => m.set(l.unit, (m.get(l.unit) ?? 0) + l.quantity), new Map<string, number>()), ([u, q]) => `${shFmtQty(q)} ${u}`).join(" · ");
  const linePk = lines.reduce((a, l) => a + (l.packageCount ?? 0), 0);
  const pk = shipment?.packageCount ?? (linePk || null);
  const anyStock = lines.some((l) => l.hasStockMovement);
  const showStock = !!shipment && shipment.status !== S.Draft && (stockOn || anyStock);
  const whole = shipment?.mode === ShipmentMode.WholeOrder;

  // ---- e-İrsaliye ----
  const dState = shipment?.despatchState ?? h?.despatchState ?? null;
  const dNumber = shipment?.despatchNumber ?? h?.despatchNumber ?? null;
  const raState = shipment?.receiptAdviceState ?? h?.receiptAdviceState ?? null;
  const attempts = shipment?.despatchAttempts ?? [];
  const advice = parseAdviceSummary(shipment?.receiptAdviceSummaryJson);
  const adviceLines = (advice?.lines ?? []).filter((l) => l.rejected > 0 || Math.abs(l.accepted - l.delivered) > EPS);
  const locked = !!shipment?.despatchLocked;
  const cancelBlocked = !!shipment && despatchBlocksCancel(shipment);
  const hasDespatch = !!shipment && (!!shipment.despatchNumber || !!shipment.despatchState || attempts.length > 0);
  const [driverFirst, driverLast] = shipment ? shDriverNames(shipment) : ["", ""];
  const driverText = [driverFirst, driverLast].filter(Boolean).join(" ");
  const dAction = shipment?.despatchAction ?? null;

  return (
    <div className="po-overlay" ref={overlayRef} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="po-drawer" ref={asideRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="po-dr-title">
        <div className="po-drawer-head">
          <div className="po-drawer-title">
            {h && <span className="po-mark big" style={{ ["--po-hue" as any]: firmHue(h.firmName) }}>{firmInitials(h.firmName)}</span>}
            <div>
              <span className="po-kicker">Sevkiyat{h ? ` · ${shFmtDate(h.shipmentDate)}` : ""}</span>
              <h2 id="po-dr-title">{h?.number ?? "Sevkiyat"}</h2>
              {h && <div className="po-drawer-sup">{h.firmName}{h.firmCode && <span className="po-codepill">{h.firmCode}</span>}</div>}
              {h && (
                <div className="po-badges">
                  <span className={`po-st ${SH_STATUS_CLASS[h.status]}`}>{SH_STATUS_LABEL[h.status]}</span>
                  {age.stuck && <span className={`po-badge ${age.state === "late" ? "err" : "warn"}`}>{age.text}</span>}
                  {dNumber && dState ? <span className={`po-badge ${DESPATCH_STATE_TONE[dState]}`} title={DESPATCH_STATE_LABEL[dState]}>e-İrsaliye {dNumber}</span>
                    : dState === "MatbuPending" ? <span className="po-badge warn">Matbu {h.waybillNo} · dönüştürülmeli</span>
                    : h.waybillNo && <span className="po-badge muted">İrsaliye {h.waybillNo}</span>}
                  {raState && raState !== "NotExpected" && <span className={`po-badge ${RA_TONE[raState]}`}>{RA_LABEL[raState]}</span>}
                  {locked && <span className="po-badge warn">{I.lock} e-İrsaliye sonucu bekleniyor</span>}
                  {h.mode != null && <span className={`po-badge ${h.mode === ShipmentMode.WholeOrder ? "info" : "muted"}`}>{SH_MODE_LABEL[h.mode]}</span>}
                  {!stockOn && h.status === S.Draft && <span className="po-badge muted">stok hareketi üretilmez</span>}
                </div>
              )}
            </div>
          </div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose}>{I.close}</button>
        </div>

        <div className="po-drawer-body">
          {!shipment && loading && <div className="po-loading"><span className="po-spin" /> Sevkiyat yükleniyor</div>}

          {!shipment && !loading && (
            <div className="po-blank">
              <span className="po-blank-icon">{I.inbox}</span>
              <b>Sevkiyat bulunamadı</b>
              {error || "Silinmiş ya da erişiminiz yok."}
              <div className="po-actrow" style={{ justifyContent: "center" }}>
                <button type="button" className="po-btn" onClick={load}>Yeniden dene</button>
                <button type="button" className="po-btn primary" onClick={onClose}>Listeye dön</button>
              </div>
            </div>
          )}

          {shipment && (
            <>
              {/* İşlemler */}
              {shipment.status === S.Draft && (
                <>
                  <div className="po-actrow">
                    {canUpdate && <button type="button" className="po-btn small primary" disabled={busy} onClick={() => onShip(shipment)}>{I.send} {locked ? "Sevk et · yeniden dene" : "Sevk et"}</button>}
                    {canUpdate && <button type="button" className="po-btn small" disabled={busy || locked} title={locked ? "e-İrsaliye sonucu netleşmeden düzenlenemez" : undefined} onClick={() => onEdit(shipment)}>{I.edit} Düzenle</button>}
                    <button type="button" className="po-btn small" disabled={busy} onClick={() => onPrint(shipment)}>{I.print} Yazdır</button>
                    {canUpdate && <button type="button" className="po-btn small danger push" disabled={busy || locked} onClick={() => onCancel(shipment)}>İptal et</button>}
                    {canDelete && <button type="button" className={`po-btn small danger ${canUpdate ? "" : "push"}`} disabled={busy || locked} onClick={() => onDelete(shipment)}>{I.trash} Sil</button>}
                  </div>
                  {locked
                    ? <div className="po-note warn">{I.lock} Son e-İrsaliye denemesinin sonucu henüz belli değil. Taslak düzenlenemez, silinemez ve iptal edilemez; "Sevk et" aynı irsaliyeyle yeniden dener (çift irsaliye oluşmaz).</div>
                    : eDespatch && <p className="po-hint">Sevk ederken e-İrsaliye düzenlenir; numarayı sistem verir.</p>}
                </>
              )}
              {shipment.status === S.Shipped && (
                <div className="po-actrow">
                  {canUpdate && <button type="button" className="po-btn small primary" disabled={busy} onClick={() => onDeliver(shipment)}>{I.receive} Teslim edildi</button>}
                  <button type="button" className="po-btn small" disabled={busy} onClick={() => onPrint(shipment)}>{I.print} Yazdır</button>
                  {canUpdate && !cancelBlocked && <button type="button" className="po-btn small danger push" disabled={busy} onClick={() => onCancel(shipment)}>İptal et</button>}
                  {cancelBlocked && <span className="po-hint push">{dState === "MatbuPending" ? "Matbu irsaliye dönüştürülmeden iptal edilemez." : "e-İrsaliye iptal edilemez; alıcı reddederse iptal açılır."}</span>}
                </div>
              )}
              {shipment.status === S.Delivered && (
                <div className="po-actrow">
                  <button type="button" className="po-btn small" disabled={busy} onClick={() => onPrint(shipment)}>{I.print} Yazdır</button>
                  <span className="po-hint push">Teslim edilmiş sevkiyat iptal edilemez.</span>
                </div>
              )}
              {shipment.status === S.Cancelled && (
                <>
                  <div className="po-note"><b>İptal edildi{shipment.cancelledAt ? ` · ${shFmtDate(shipment.cancelledAt)}` : ""}</b>{shipment.cancelReason ? ` — ${shipment.cancelReason}` : ""}</div>
                  <div className="po-actrow">
                    <button type="button" className="po-btn small" disabled={busy} onClick={() => onPrint(shipment)}>{I.print} Yazdır</button>
                  </div>
                </>
              )}

              {/* Rakamlar */}
              <div className="po-figs">
                <div className="po-fig"><span className="l">Toplam miktar</span><span className="v">{unitTotals || "—"}</span><span className="s">{lines.length} sipariş satırı</span></div>
                <div className="po-fig"><span className="l">Koli / ağırlık</span><span className="v">{pk != null ? `${pk} koli` : "—"}</span><span className="s">{shipment.grossWeightKg != null ? `${shFmtQty(shipment.grossWeightKg)} kg` : "ağırlık girilmedi"}</span></div>
                {shipment.status === S.Draft && <div className="po-fig"><span className="l">Sevk tarihi</span><span className="v">{shFmtDate(shipment.shipmentDate)}</span><span className="s">henüz sevk edilmedi</span></div>}
                {shipment.status === S.Shipped && <div className={`po-fig ${age.state}`}><span className="l">Yolda</span><span className="v">{age.days ?? 0} gün</span><span className="s">sevk {shFmtDateTime(shipment.shippedAt)}</span></div>}
                {shipment.status === S.Delivered && <div className="po-fig"><span className="l">Teslim</span><span className="v">{shFmtDateTime(shipment.deliveredAt)}</span><span className="s">{shipment.receivedBy || "—"}</span></div>}
                {shipment.status === S.Cancelled && <div className="po-fig late"><span className="l">İptal</span><span className="v">{shFmtDate(shipment.cancelledAt)}</span><span className="s">{anyStock ? "stok çıkışları geri alındı" : "stok hareketi yoktu"}</span></div>}
              </div>

              {/* e-İrsaliye */}
              {hasDespatch && (
                <section className="po-sec sh-ed">
                  <h3>e-İrsaliye
                    {dState && <span className={`po-badge ${DESPATCH_STATE_TONE[dState]}`}>{DESPATCH_STATE_LABEL[dState]}</span>}
                    {raState && raState !== "NotExpected" && <span className={`po-badge ${RA_TONE[raState]}`}>{RA_LABEL[raState]}</span>}
                  </h3>

                  {dState === "Failed" && (
                    <div className="po-note err" role="alert">
                      {shipment.despatchLastError || "e-İrsaliye GİB'de / entegratörde başarısız oldu."}
                      {shipment.despatchGibStatusCode && !(shipment.despatchLastError || "").includes(String(shipment.despatchGibStatusCode)) ? ` (GİB ${shipment.despatchGibStatusCode})` : ""}
                      <div className="sh-ed-sub">{dAction === "resend"
                        ? "Geçici bir GİB hatası: aynı içerikle yeniden gönderilebilir."
                        : "Bu belge geçersiz sayılır. Bilgileri düzeltip yeni e-İrsaliye düzenleyin; stok yeniden düşülmez. Araçtaki nüshayı yenisiyle değiştirin."}</div>
                    </div>
                  )}
                  {dState === "MatbuPending" && (
                    <div className="po-note warn">Matbu irsaliye ({shipment.printedWaybillNo}{shipment.printedWaybillDate ? `, ${shFmtDate(shipment.printedWaybillDate)}` : ""}) ile sevk edildi. En geç izleyen gün MATBUDAN e-İrsaliyeye dönüştürülmeli (Kılavuz §12).</div>
                  )}
                  {raState === "Rejected" && <div className="po-note err">Alıcı irsaliyeyi reddetti. Mal geri geliyorsa sevkiyatı iptal edebilirsiniz; aksi halde alıcıyla görüşün.</div>}
                  {raState === "PartiallyAccepted" && <div className="po-note warn">Alıcı irsaliyeyi kısmen kabul etti; reddedilen miktarlar aşağıda.</div>}
                  {(advice?.warnings ?? []).map((w, i) => w.message && <div key={i} className="po-note warn">{w.message}</div>)}

                  {/* Künye yalnız düzenlenmiş belge (ya da matbu irsaliye) varken: belirsiz/başarısız taslakta boş kutular gösterilmez */}
                  {(shipment.despatchNumber || shipment.printedWaybillNo) && (
                    <dl className="po-facts">
                      {shipment.despatchNumber && <>
                        <div><dt>e-İrsaliye no</dt><dd>{shipment.despatchNumber}{shipment.despatchIssuedAt && <small>{shFmtDateTime(shipment.despatchIssuedAt)}</small>}</dd></div>
                        <div><dt>Alıcı</dt><dd>{shipment.despatchReceiverRegistered == null ? "—" : shipment.despatchReceiverRegistered ? "e-İrsaliye mükellefi" : "Kayıtlı değil"}
                          {shipment.despatchReceiverRegistered === false && <small>GİB sanal alıcısına gitti; yanıt beklenmez</small>}</dd></div>
                        <div className="wide"><dt>ETTN</dt><dd className="sh-ed-uuid">{shipment.despatchUuid || "—"}</dd></div>
                      </>}
                      {raState && raState !== "NotExpected" && (
                        <div><dt>İrsaliye Yanıtı</dt><dd>{RA_LABEL[raState]}
                          <small>{raState === "Pending"
                            ? (advice?.dueDate ? `son gün ${shFmtDate(advice.dueDate)}` : "7 gün içinde beklenir")
                            : [advice?.number, shipment.receiptAdviceAt && shFmtDateTime(shipment.receiptAdviceAt), advice?.isLate ? "süresi geçmiş yanıt" : ""].filter(Boolean).join(" · ") || "—"}</small>
                          {shipment.deliveredSource === 2 && <small>7 gün yanıt gelmedi: kabul sayıldı</small>}
                        </dd></div>
                      )}
                      {shipment.printedWaybillNo && <div><dt>Matbu irsaliye</dt><dd>{shipment.printedWaybillNo}{shipment.printedWaybillDate && <small>{shFmtDate(shipment.printedWaybillDate)}</small>}</dd></div>}
                      {shipment.despatchNumber && <div><dt>Entegratör</dt><dd>{shipment.despatchProvider || "—"}{shipment.despatchLastSyncAt && <small>son eşitleme {shFmtDateTime(shipment.despatchLastSyncAt)}</small>}</dd></div>}
                    </dl>
                  )}

                  {adviceLines.length > 0 && (
                    <div className="po-table-wrap tight">
                      <table className="po-table small">
                        <thead><tr><th>Satır</th><th className="num">Gönderilen</th><th className="num">Kabul</th><th className="num">Red</th><th className="po-hide-sm">Sebep</th></tr></thead>
                        <tbody>
                          {adviceLines.map((l) => (
                            <tr key={`${l.lineNo}-${l.shipmentLineId}`} className="static">
                              <td>{l.name}</td>
                              <td className="num">{shFmtQty(l.delivered)}</td>
                              <td className="num">{shFmtQty(l.accepted)}</td>
                              <td className="num"><b className="sh-ed-rej">{shFmtQty(l.rejected)}</b></td>
                              <td className="po-hide-sm">{l.reason || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="po-actrow">
                    {shipment.despatchDocumentId && <button type="button" className="po-btn small" disabled={busy} onClick={() => onPrint(shipment)}>{I.doc} e-İrsaliyeyi görüntüle</button>}
                    {canUpdate && onDespatch && shipment.despatchDocumentId && <button type="button" className="po-btn small" disabled={busy} onClick={() => onDespatch(shipment, "refresh")}>{I.refresh} Durumu yenile</button>}
                    {canUpdate && onDespatch && dAction === "resend" && <button type="button" className="po-btn small primary" disabled={busy} onClick={() => onDespatch(shipment, "resend")}>{I.send} Yeniden gönder</button>}
                    {canUpdate && onDespatch && dAction === "reissue" && <button type="button" className="po-btn small primary" disabled={busy} onClick={() => onDespatch(shipment, "reissue")}>{I.send} Yeni e-İrsaliye düzenle</button>}
                    {canUpdate && onDespatch && dAction === "matbudan" && <button type="button" className="po-btn small primary" disabled={busy} onClick={() => onDespatch(shipment, "matbudan")}>{I.send} e-İrsaliyeye dönüştür</button>}
                  </div>

                  {attempts.length > 0 && (
                    <details className="sh-ed-attempts" open={shipment.status === S.Draft}>
                      <summary>Gönderim denemeleri <em>{attempts.length}</em></summary>
                      <ul>
                        {attempts.map((a) => (
                          <li key={a.id}>
                            <div className="sh-ed-att-top">
                              <b>{DESPATCH_KIND_LABEL[a.kind] ?? "Deneme"}</b>
                              <span className={`po-badge ${DESPATCH_OUTCOME_TONE[a.outcome]}`}>{DESPATCH_OUTCOME_LABEL[a.outcome]}</span>
                              {a.despatchNumber && <span className="po-codepill">{a.despatchNumber}</span>}
                            </div>
                            <small>{shFmtDateTime(a.requestedAt)} · {a.requestedBy || "—"}{a.httpStatus ? ` · HTTP ${a.httpStatus}` : ""}{a.errorCode ? ` · ${a.errorCode}` : ""}</small>
                            {a.errorMessage && <small className="sh-ed-att-err">{a.errorMessage}</small>}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </section>
              )}

              {/* Künye */}
              <dl className="po-facts">
                <div className="wide"><dt>Teslimat adresi</dt>
                  <dd>{shipment.deliveryTitle || shipment.firmName}
                    <small>{[shipment.deliveryAddress, [shipment.deliveryDistrict, shipment.deliveryCity].filter(Boolean).join(" / "), shipment.deliveryPostalCode].filter(Boolean).join(" · ") || "Adres girilmedi"}</small>
                    <small>{shipment.deliveryContact}{shipment.deliveryContact && shipment.deliveryPhone ? " · " : ""}{shipment.deliveryPhone && <a href={`tel:${shipment.deliveryPhone}`}>{shipment.deliveryPhone}</a>}{!shipment.deliveryContact && !shipment.deliveryPhone && "İletişim bilgisi yok"}</small>
                  </dd></div>
                <div><dt>Nakliyeci / kargo</dt><dd>{shipment.carrier || "—"}
                  {(shipment.vehiclePlate || shipment.trailerPlate) && <small>
                    {shipment.vehiclePlate && <span className="po-codepill sh-plate">{shipment.vehiclePlate}</span>}
                    {shipment.trailerPlate && <> dorse <span className="po-codepill sh-plate">{shipment.trailerPlate}</span></>}
                    {shipment.plateIsForeign && " · yabancı plaka"}
                  </small>}
                  {shipment.carrierTaxNumber && <small>Taşıyıcı VKN/TCKN {shipment.carrierTaxNumber}</small>}
                </dd></div>
                <div><dt>Şoför</dt><dd>{driverText || shipment.driverName || "—"}{shipment.driverTckn && <small>TCKN {shipment.driverTckn}</small>}</dd></div>
                <div><dt>Takip no</dt><dd>{shipment.trackingNo || "—"}</dd></div>
                <div><dt>{shipment.despatchNumber ? "Resmi irsaliye (e-İrsaliye)" : "Resmi irsaliye"}</dt><dd>{shipment.waybillNo || "—"}{shipment.waybillDate && <small>{shFmtDate(shipment.waybillDate)}</small>}</dd></div>
                <div><dt>Oluşturan</dt><dd>{shipment.createdBy || "—"}<small>{shFmtDateTime(shipment.createdAt)}</small></dd></div>
                {shipment.updatedAt && <div><dt>Güncelleyen</dt><dd>{shipment.updatedBy || "—"}<small>{shFmtDateTime(shipment.updatedAt)}</small></dd></div>}
                <div className="wide"><dt>Not</dt><dd>{shipment.note || "—"}</dd></div>
              </dl>

              {/* Siparişler */}
              <section className="po-sec">
                <h3>Siparişler <em>{lines.length}</em></h3>
                {lines.length === 0 ? (
                  <div className="po-blank small"><b>Bu sevkiyatta satır yok</b></div>
                ) : (
                  <>
                    <div className="po-table-wrap tight">
                      <table className="po-table small">
                        <thead><tr><th>#</th><th>Sipariş</th><th className="po-hide-sm">Sipariş tarihi</th><th className="num">Sevk miktarı</th><th className="num">Koli</th><th className="num">Sipariş toplamı</th>{showStock && <th>Stok</th>}</tr></thead>
                        <tbody>
                          {lines.map((l, i) => {
                            const rem = l.orderQuantity - l.orderShipped;
                            return (
                              <tr key={l.shipmentLineId} className="static">
                                <td>{i + 1}</td>
                                <td><button type="button" className="po-link" onClick={() => navigate(`/orders/detail/${l.orderId}`)}>{l.modelCode || "Model yok"}</button>{l.modelName && <div className="po-sub">{l.modelName}</div>}</td>
                                <td className="po-hide-sm">{shFmtDate(l.orderDate)}</td>
                                <td className="num"><span className="po-amt">{shFmtQty(l.quantity)} {l.unit}</span>{whole && <div className="po-sub">No {shOrderNo(l.orderId)}</div>}</td>
                                <td className="num">{l.packageCount ?? "—"}</td>
                                {whole ? (
                                  // Bütün sipariş: satır siparişin tamamıdır (salt okunur); taslakta sevk anında güncel miktara eşitlenir
                                  <td className="num">{shFmtQty(l.orderQuantity)}<div><span className="po-badge info">Tamamı</span></div>
                                    {shipment.status === S.Draft && <div className="po-sub">sevkte güncel miktar</div>}
                                  </td>
                                ) : (
                                  <td className="num">{shFmtQty(l.orderShipped)} / {shFmtQty(l.orderQuantity)}
                                    {rem > EPS ? <div className="po-sub">kalan {shFmtQty(rem)}</div> : <div><span className="po-badge ok">Tam</span></div>}
                                    {shipment.status === S.Draft && <div className="po-sub">bu taslak dahil</div>}
                                  </td>
                                )}
                                {showStock && (
                                  <td>{!l.hasStockMovement ? <span className="po-badge muted">hareket yok</span>
                                    : shipment.status === S.Cancelled ? <span className="po-badge warn">Geri alındı</span>
                                    : <span className="po-badge ok">Düşüldü</span>}</td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="po-totals">
                      <div className="po-total-row"><span>Sipariş</span><b>{lines.length}</b></div>
                      <div className="po-total-row"><span>Koli</span><b>{linePk || "—"}</b></div>
                      <div className="po-total-row grand"><span>Toplam</span><b>{unitTotals || "—"}</b></div>
                    </div>
                  </>
                )}
              </section>

              {/* Geçmiş */}
              <section className="po-sec">
                <h3>Geçmiş</h3>
                <div className="po-timeline">
                  <div className="po-tl"><b>Oluşturuldu</b><small>{shFmtDateTime(shipment.createdAt)} · {shipment.createdBy || "—"}</small></div>
                  {shipment.shippedAt && (
                    <div className="po-tl">
                      <b>Sevk edildi</b>
                      <small>{shFmtDateTime(shipment.shippedAt)} · {[shipment.carrier, shipment.vehiclePlate].filter(Boolean).join(" · ") || "—"}</small>
                      {stockOn && (
                        <ul className="po-tl-lines">
                          {lines.map((l) => <li key={l.shipmentLineId}>{l.modelCode || "Model yok"} — {shFmtQty(l.quantity)} {l.unit}{!l.hasStockMovement && <span className="po-badge muted">stok hareketi yok</span>}</li>)}
                        </ul>
                      )}
                    </div>
                  )}
                  {shipment.despatchIssuedAt && shipment.despatchNumber && (
                    <div className="po-tl"><b>e-İrsaliye {shipment.despatchNumber}</b><small>{shFmtDateTime(shipment.despatchIssuedAt)}{dState ? ` · ${DESPATCH_STATE_LABEL[dState]}` : ""}</small></div>
                  )}
                  {shipment.receiptAdviceAt && raState && raState !== "Pending" && raState !== "NotExpected" && (
                    <div className="po-tl"><b>İrsaliye Yanıtı · {RA_LABEL[raState]}</b><small>{shFmtDateTime(shipment.receiptAdviceAt)}{advice?.number ? ` · ${advice.number}` : ""}</small></div>
                  )}
                  {shipment.status === S.Shipped && <div className="po-tl"><b>Yolda</b><small>{age.text} · {raState === "Pending" ? "İrsaliye Yanıtı bekleniyor" : "teslim onayı bekleniyor"}</small></div>}
                  {shipment.deliveredAt && <div className="po-tl"><b>Teslim edildi</b><small>{shFmtDateTime(shipment.deliveredAt)} · {shipment.receivedBy || "—"}{shipment.deliveredSource === 1 ? " · e-İrsaliye yanıtıyla" : shipment.deliveredSource === 2 ? " · 7 gün yanıtsız, kabul sayıldı" : ""}</small></div>}
                  {shipment.status === S.Cancelled && <div className="po-tl"><b>İptal edildi</b><small>{shFmtDateTime(shipment.cancelledAt)}{shipment.cancelReason ? ` — ${shipment.cancelReason}` : ""}{anyStock ? " · stok çıkışları geri alındı" : ""}</small></div>}
                </div>
                {shipment.status === S.Draft && <p className="po-hint">Henüz sevk edilmedi.</p>}
              </section>

              {hasModule("receivables") && has("receivables:view") && (
                <div className="po-actrow">
                  <button type="button" className="po-link" onClick={() => navigate(`/receivables/firm/${shipment.firmId}`)}>Firma cari hesabı</button>
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
};

export default ShipmentDrawer;
